import { useSetAtom } from 'jotai';
import { Card } from 'primereact/card';
import { Skeleton } from 'primereact/skeleton';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import type { TooltipProps } from 'recharts';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { PortfolioForwardProjectionResponse } from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import { selectWidgetAIAtom } from '~/atoms/dashboardAtom';
import { openPopoutWindow } from '~/utils/popoutUtils';
import { MetricLabel } from '../../common/MetricLabel';
import { NoDataAvailableTemplate } from '../../common/NoDataAvailableTemplate';
import { WidgetHeader } from '../../common/WidgetHeader';
import { exportWidgetToPDF } from '../common/pdfExportUtils';

export type ForwardReturnsData = PortfolioForwardProjectionResponse;

type ChartDataPoint = {
  date: string;
  current: number | null;
  proposed: number | null;
  benchmark: number | null;
  proposedUpper: number | null;
  proposedLower: number | null;
  fullDate: string;
};

type CustomTooltipProps = TooltipProps<number, string> & {
  chartDataLookup: Map<string, ChartDataPoint>;
  benchmarkName?: string | null;
  active?: boolean;
  payload?: Array<{
    dataKey?: string;
    value?: number | string;
    color?: string;
  }>;
  label?: string;
};

const tooltipStyle = {
  backgroundColor: 'var(--bq-tooltip-bg)',
  border: '1px solid var(--bq-tooltip-border)',
  borderRadius: '6px',
  padding: '8px',
  color: 'var(--bq-tooltip-text)',
} as const;

const CustomTooltip = memo<CustomTooltipProps>(
  ({ active, payload, label, chartDataLookup, benchmarkName }) => {
    if (!active || !payload || !label) return null;

    const dataPoint = chartDataLookup.get(label as string);

    const filteredPayload = (payload || []).filter(
      (item) =>
        item.dataKey !== 'proposedUpper' && item.dataKey !== 'proposedLower',
    );

    if (filteredPayload.length === 0) return null;

    return (
      <div style={tooltipStyle}>
        <p
          style={{ margin: '0 0 4px 0', fontWeight: 500 }}
        >{`Date: ${label}`}</p>
        {filteredPayload.map((item, index) => (
          <p key={index} style={{ margin: '2px 0', fontSize: '14px' }}>
            <span style={{ color: item.color }}>
              {item.dataKey === 'current'
                ? 'Current Portfolio'
                : item.dataKey === 'benchmark'
                  ? benchmarkName || 'Benchmark'
                  : 'AI Portfolio'}
            </span>
            {' : '}
            {typeof item.value === 'number'
              ? `${item.value.toFixed(2)}%`
              : item.value}
          </p>
        ))}
        {dataPoint &&
          dataPoint.proposedUpper !== null &&
          dataPoint.proposedLower !== null && (
            <div
              style={{
                marginTop: '8px',
                paddingTop: '8px',
                borderTop: '1px solid var(--bq-tooltip-border)',
              }}
            >
              <p
                style={{
                  margin: '2px 0',
                  fontSize: '12px',
                  color: 'var(--bq-color-neutral)',
                }}
              >
                68% Confidence Interval:
              </p>
              <p
                style={{
                  margin: '2px 0',
                  fontSize: '14px',
                  color: 'var(--bq-color-positive)',
                }}
              >
                Upper: {dataPoint.proposedUpper.toFixed(2)}%
              </p>
              <p
                style={{
                  margin: '2px 0',
                  fontSize: '14px',
                  color: 'var(--bq-color-negative)',
                }}
              >
                Lower: {dataPoint.proposedLower.toFixed(2)}%
              </p>
            </div>
          )}
      </div>
    );
  },
);

CustomTooltip.displayName = 'CustomTooltip';

export type ForwardReturnsWidgetProps = {
  data: ForwardReturnsData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  snapshotId?: string | null;
  benchmarkName?: string | null;
  portfolioId?: string | null;
  portfolioName?: string | null;
};

export const ForwardReturnsWidget: React.FC<ForwardReturnsWidgetProps> = ({
  data,
  isLoading,
  error,
  refetch,
  handleRemoveWidget,
  widgetId,
  title = 'Forward & Real Returns',
  snapshotId,
  benchmarkName,
  portfolioId,
  portfolioName,
}) => {
  const setSelectWidgetAI = useSetAtom(selectWidgetAIAtom);
  const widgetRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );

  // Add popout functionality using dedicated endpoint
  const handlePopout = useCallback(() => {
    openPopoutWindow({
      widgetType: 'forward-returns',
      snapshotId: snapshotId || undefined,
      benchmarkName: benchmarkName || undefined,
      title: title || 'Forward & Real Returns',
      portfolioId: portfolioId || undefined,
      portfolioName: portfolioName || undefined,
    });
  }, [snapshotId, benchmarkName, title, portfolioId, portfolioName]);

  // Export to PDF
  const handleExport = useCallback(async () => {
    if (!widgetRef.current || !data) {
      console.warn('Cannot export: widget element or data not available');
      return;
    }

    // Build summary metrics for header
    let headerRightText = '';
    if (data?.summary) {
      const summaryParts: string[] = [];
      if (data.summary.current_total_return !== null) {
        summaryParts.push(
          `Current: ${data.summary.current_total_return.toFixed(2)}%`,
        );
      }
      if (data.summary.benchmark_total_return !== null) {
        summaryParts.push(
          `${benchmarkName || 'Benchmark'}: ${data.summary.benchmark_total_return.toFixed(2)}%`,
        );
      }
      if (data.summary.proposed_total_return !== null) {
        summaryParts.push(
          `AI: ${data.summary.proposed_total_return.toFixed(2)}%`,
        );
      }
      if (summaryParts.length > 0) {
        headerRightText = summaryParts.join(' | ');
      }
    }

    setIsExporting(true);
    try {
      await exportWidgetToPDF({
        widgetElement: widgetRef.current,
        title,
        portfolioName,
        headerRightText,
      });
    } catch (error) {
      console.error('Failed to export PDF:', error);
    } finally {
      setIsExporting(false);
    }
  }, [widgetRef, data, title, portfolioName, benchmarkName]);

  // Transform data for chart
  const chartData = useMemo(() => {
    if (!data?.dates) return [];

    const todayIdx = data?.today_index ?? -1;

    const formatted = data.dates.map((date, index) => {
      // Only show confidence bounds after "today"
      const isForward = todayIdx >= 0 && index > todayIdx;

      // The API returns upper/lower swapped, so we swap them here
      const apiUpper = data.proposed_portfolio_upper?.[index] ?? null;
      const apiLower = data.proposed_portfolio_lower?.[index] ?? null;

      return {
        date: new Date(date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
        current: data.current_portfolio?.[index] ?? null,
        proposed: data.proposed_portfolio?.[index] ?? null,
        benchmark: data.benchmark?.[index] ?? null,
        // Confidence bounds - only for forward projections (swap upper/lower since API has them backwards)
        proposedUpper:
          isForward && apiUpper !== null && apiLower !== null
            ? Math.max(apiUpper, apiLower)
            : null,
        proposedLower:
          isForward && apiUpper !== null && apiLower !== null
            ? Math.min(apiUpper, apiLower)
            : null,
        fullDate: date,
      };
    });

    return formatted;
  }, [data]);

  // O(1) lookup map for tooltip performance
  const chartDataLookup = useMemo(() => {
    const map = new Map<string, ChartDataPoint>();
    for (const point of chartData) {
      map.set(point.date, point);
    }
    return map;
  }, [chartData]);

  // Get batch date indices for reference lines
  const currentBatchIndices = useMemo(() => {
    if (!data?.current_batch_dates || !data?.dates || !chartData.length)
      return [];
    return data.current_batch_dates
      .map((batchDate) => {
        const index = data.dates.indexOf(batchDate);
        if (index === -1) return null;
        return { date: chartData[index]?.date, index };
      })
      .filter((item): item is { date: string; index: number } => item !== null);
  }, [data, chartData]);

  const proposedBatchIndices = useMemo(() => {
    if (!data?.proposed_batch_dates || !data?.dates || !chartData.length)
      return [];
    const indices = data.proposed_batch_dates
      .map((batchDate) => {
        const index = data.dates.indexOf(batchDate);
        if (index === -1) return null;
        return { date: chartData[index]?.date, index, rawDate: batchDate };
      })
      .filter(
        (item): item is { date: string; index: number; rawDate: string } =>
          item !== null,
      );

    console.log('Proposed batch indices:', indices);
    return indices;
  }, [data, chartData]);

  // Find the dividing line between historical and forward
  const todayIndex = useMemo(() => {
    return data?.today_index ?? -1;
  }, [data]);

  const noData = useMemo(() => {
    return !data || !data.dates || data.dates.length === 0;
  }, [data]);

  const hasBenchmarkData = useMemo(() => {
    return data?.benchmark && data.benchmark.some((val) => val !== null);
  }, [data]);

  const hasCurrentData = useMemo(() => {
    return (
      data?.current_portfolio &&
      data.current_portfolio.some((val) => val !== null)
    );
  }, [data]);

  // Calculate Y-axis ticks and domain using fixed percentage intervals
  const yAxisConfig = useMemo(() => {
    if (!chartData.length) return { ticks: undefined, domain: undefined };

    // Find min and max values across all data
    const allValues = chartData.flatMap((d) =>
      [
        d.current,
        d.proposed,
        d.benchmark,
        d.proposedUpper,
        d.proposedLower,
      ].filter((v): v is number => v !== null),
    );

    if (allValues.length === 0) return { ticks: undefined, domain: undefined };

    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);

    // Define available tick values: 0%, ±5%, ±10%, ±15%, ±20%, ±30%
    const availableTicks = [-30, -20, -15, -10, -5, 0, 5, 10, 15, 20, 30];

    // Select ticks that cover the data range
    const selectedTicks = availableTicks.filter(
      (tick) =>
        tick >= Math.floor(minValue) - 5 && tick <= Math.ceil(maxValue) + 5,
    );

    // Ensure we have at least 3 ticks and always include 0
    if (!selectedTicks.includes(0)) {
      selectedTicks.push(0);
      selectedTicks.sort((a, b) => a - b);
    }

    // Set domain to first and last tick
    const domain = [
      selectedTicks[0],
      selectedTicks[selectedTicks.length - 1],
    ] as [number, number];

    return { ticks: selectedTicks, domain };
  }, [chartData]);

  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full hover-container" id={widgetId} ref={widgetRef}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          onExport={data ? handleExport : undefined}
          isExporting={isExporting}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="Something went wrong."
          isError
        />
      </div>
    );
  }

  if (!snapshotId) {
    return (
      <div className="h-full hover-container" id={widgetId} ref={widgetRef}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="No Snapshot Available."
          subTitle="Run portfolio analysis to generate snapshot"
        />
      </div>
    );
  }

  if (noData) {
    return (
      <div className="h-full hover-container" id={widgetId} ref={widgetRef}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          title="No forward projection data available."
          subTitle="Run portfolio analysis to generate forward projection"
          icon="pi pi-chart-line"
        />
      </div>
    );
  }

  return (
    <div
      className="hover-container h-full flex flex-column"
      id={widgetId}
      ref={widgetRef}
    >
      <WidgetHeader
        title={title}
        removeWidget={removeWidget}
        refetch={() => refetch()}
        setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
        onPopout={handlePopout}
        onExport={handleExport}
        isExporting={isExporting}
        widgetId={widgetId}
      />

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-3 h-full">
          <Card
            className="h-full"
            pt={{
              content: {
                className: 'p-0 m-0 h-full flex flex-column',
              },
              body: {
                className: 'h-full',
              },
            }}
          >
            {/* Performance Summary */}
            <div
              className="pb-2 border-bottom-1 mb-2 flex-shrink-0"
              style={{ borderColor: 'var(--bq-border-muted)' }}
            >
              <div className="grid">
                {hasCurrentData && (
                  <div className={hasBenchmarkData ? 'col-4' : 'col-6'}>
                    <div className="text-center">
                      <div
                        className="text-xl font-bold"
                        style={{ color: 'var(--bq-chart-current)' }}
                      >
                        {data?.summary.current_total_return?.toFixed(2) ||
                          '0.00'}
                        %
                      </div>
                      <div
                        className="text-xs"
                        style={{ color: 'var(--bq-text-tertiary)' }}
                      >
                        Current Portfolio
                      </div>
                    </div>
                  </div>
                )}
                {hasBenchmarkData && (
                  <div className={hasCurrentData ? 'col-4' : 'col-6'}>
                    <div className="text-center">
                      <div
                        className="text-xl font-bold"
                        style={{ color: 'var(--bq-chart-benchmark)' }}
                      >
                        {data?.summary.benchmark_total_return?.toFixed(2) ||
                          '0.00'}
                        %
                      </div>
                      <div
                        className="text-xs"
                        style={{ color: 'var(--bq-text-tertiary)' }}
                      >
                        {benchmarkName || 'Benchmark'}
                      </div>
                    </div>
                  </div>
                )}
                <div
                  className={
                    hasCurrentData || hasBenchmarkData ? 'col-4' : 'col-12'
                  }
                >
                  <div className="text-center">
                    <div
                      className="text-xl font-bold"
                      style={{ color: 'var(--bq-chart-ai)' }}
                    >
                      {data?.summary.proposed_total_return?.toFixed(2) ||
                        '0.00'}
                      %
                    </div>
                    <div
                      className="text-xs"
                      style={{ color: 'var(--bq-text-tertiary)' }}
                    >
                      AI Portfolio
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Chart */}
            <div className="flex-1 min-h-0" style={{ minHeight: '150px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--bq-chart-grid)"
                  />
                  <XAxis
                    dataKey="date"
                    stroke="var(--bq-color-neutral)"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="var(--bq-color-neutral)"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `${value.toFixed(1)}%`}
                    ticks={yAxisConfig.ticks}
                    domain={yAxisConfig.domain}
                  />
                  <Tooltip
                    content={
                      <CustomTooltip
                        chartDataLookup={chartDataLookup}
                        benchmarkName={benchmarkName}
                      />
                    }
                  />
                  <Legend
                    wrapperStyle={{
                      color: 'var(--bq-tooltip-text)',
                      paddingTop: '10px',
                    }}
                    formatter={(value) =>
                      value === 'current'
                        ? 'Current Portfolio'
                        : value === 'benchmark'
                          ? benchmarkName || 'Benchmark'
                          : 'AI Portfolio'
                    }
                  />
                  {todayIndex >= 0 && chartData[todayIndex] && (
                    <ReferenceLine
                      x={chartData[todayIndex].date}
                      stroke="var(--bq-color-neutral)"
                      strokeDasharray="5 5"
                      label={{
                        value: 'Today',
                        position: 'top',
                        fill: 'var(--bq-color-neutral)',
                        fontSize: 12,
                      }}
                    />
                  )}
                  {/* Confidence interval cone - renders from lower to upper bound */}
                  <Area
                    type="monotone"
                    dataKey="proposedUpper"
                    stroke="none"
                    fill="var(--bq-color-positive)"
                    fillOpacity={0.2}
                    connectNulls
                    legendType="none"
                    isAnimationActive={false}
                  />
                  {/* Mask out area below lower bound - red for losses */}
                  <Area
                    type="monotone"
                    dataKey="proposedLower"
                    stroke="none"
                    fill="var(--bq-color-negative)"
                    fillOpacity={0.25}
                    connectNulls
                    legendType="none"
                    isAnimationActive={false}
                  />
                  {/* Current Portfolio */}
                  {hasCurrentData && (
                    <Line
                      type="monotone"
                      dataKey="current"
                      stroke="var(--bq-chart-current)"
                      strokeWidth={2}
                      dot={false}
                      name="current"
                      connectNulls
                      activeDot={{ r: 6 }}
                      isAnimationActive={false}
                    />
                  )}
                  {/* Benchmark */}
                  {hasBenchmarkData && (
                    <Line
                      type="monotone"
                      dataKey="benchmark"
                      stroke="var(--bq-chart-benchmark)"
                      strokeWidth={2}
                      dot={false}
                      name="benchmark"
                      connectNulls
                      activeDot={{ r: 6 }}
                      isAnimationActive={false}
                    />
                  )}
                  {/* AI Portfolio */}
                  <Line
                    type="monotone"
                    dataKey="proposed"
                    stroke="var(--bq-chart-ai)"
                    strokeWidth={2}
                    dot={false}
                    name="proposed"
                    activeDot={{ r: 6 }}
                    isAnimationActive={false}
                  />
                  {/* Batch date indicators - only show most recent from each */}
                  {currentBatchIndices.length > 0 && (
                    <ReferenceLine
                      key="current-batch-latest"
                      x={
                        currentBatchIndices[currentBatchIndices.length - 1].date
                      }
                      stroke="var(--bq-chart-current)"
                      strokeDasharray="3 3"
                      strokeWidth={2}
                      strokeOpacity={0.5}
                    />
                  )}
                  {proposedBatchIndices.length > 0 && (
                    <ReferenceLine
                      key="proposed-batch-latest"
                      x={
                        proposedBatchIndices[proposedBatchIndices.length - 1]
                          .date
                      }
                      stroke="var(--bq-chart-ai)"
                      strokeDasharray="3 3"
                      strokeWidth={2}
                      strokeOpacity={0.5}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Additional Info */}
            <div
              className="mt-2 pt-2 border-top-1 flex-shrink-0"
              style={{ borderColor: 'var(--bq-border-muted)' }}
            >
              <div className="grid">
                <div className="col-4">
                  <div className="text-center">
                    <div
                      className="text-base font-semibold"
                      style={{ color: 'var(--bq-chart-ai)' }}
                    >
                      {data?.summary.proposed_total_return?.toFixed(2) ||
                        '0.00'}
                      %
                    </div>
                    <div
                      className="text-xs"
                      style={{ color: 'var(--bq-text-tertiary)' }}
                    >
                      Expected Annual Return
                    </div>
                  </div>
                </div>
                <div className="col-4">
                  <div className="flex align-items-center justify-content-center h-full">
                    <MetricLabel
                      label="Methodology"
                      description={
                        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                          <div className="mb-3">
                            <h4
                              className="text-base font-semibold mb-2"
                              style={{ color: 'var(--bq-text-primary)' }}
                            >
                              How Forward Returns Are Generated
                            </h4>
                            <p className="mb-2">
                              The chart displays{' '}
                              <strong>actual historical returns</strong> for all
                              portfolios before today's date, showing real
                              performance based on market data. From today
                              forward, this projection uses a{' '}
                              <strong>GARCH</strong> (Generalized Autoregressive
                              Conditional Heteroskedasticity) statistical model
                              combined with <strong>EWMA</strong> (Exponentially
                              Weighted Moving Average) expected returns to
                              simulate realistic portfolio performance over the
                              shown period.
                            </p>
                          </div>

                          <div className="mb-3">
                            <h4
                              className="text-base font-semibold mb-2"
                              style={{ color: 'var(--bq-text-primary)' }}
                            >
                              Statistical Approach
                            </h4>

                            <div className="mb-2">
                              <p className="font-semibold mb-1">
                                1. GARCH(1,1) Model
                              </p>
                              <p className="mb-1">
                                For each asset in the portfolio, a separate
                                GARCH model is fitted to historical daily
                                returns. This captures:
                              </p>
                              <ul className="list-disc ml-4 mb-0">
                                <li>
                                  Volatility clustering (periods of high/low
                                  volatility tend to persist)
                                </li>
                                <li>
                                  Fat tails (extreme market events occur more
                                  frequently than normal distributions predict)
                                </li>
                                <li>
                                  Time-varying volatility (market conditions
                                  change over time)
                                </li>
                              </ul>
                            </div>

                            <div className="mb-2">
                              <p className="font-semibold mb-1">
                                2. EWMA Expected Returns
                              </p>
                              <p>
                                Each asset's target return is calculated using
                                an exponentially weighted moving average of
                                historical returns, which gives more weight to
                                recent performance while considering longer-term
                                trends.
                              </p>
                            </div>

                            <div className="mb-2">
                              <p className="font-semibold mb-1">
                                3. Drift Adjustment
                              </p>
                              <p>
                                The GARCH simulation is adjusted with a daily
                                drift term to ensure the projected path hits the
                                EWMA target return at the projection horizon,
                                balancing statistical realism with directional
                                expectations.
                              </p>
                            </div>
                          </div>

                          <div className="mb-3">
                            <h4
                              className="text-base font-semibold mb-2"
                              style={{ color: 'var(--bq-text-primary)' }}
                            >
                              Confidence Intervals (68% CI)
                            </h4>
                            <p className="mb-2">
                              The shaded area represents a 68% confidence
                              interval (approximately ±1 standard deviation),
                              generated from the GARCH model's variance
                              forecasts:
                            </p>
                            <ul className="list-disc ml-4 mb-2">
                              <li>
                                <strong>Upper Bound:</strong> The 68th
                                percentile projection, representing optimistic
                                but plausible outcomes
                              </li>
                              <li>
                                <strong>Lower Bound:</strong> The 32nd
                                percentile projection, representing pessimistic
                                but plausible outcomes
                              </li>
                            </ul>
                            <p>
                              This means there's a 68% probability that the
                              actual returns will fall within this range,
                              assuming market conditions follow historical
                              patterns.
                            </p>
                          </div>

                          <div className="mb-3">
                            <h4
                              className="text-base font-semibold mb-2"
                              style={{ color: 'var(--bq-text-primary)' }}
                            >
                              Portfolio Projections
                            </h4>
                            <p className="mb-1">
                              Each portfolio line shows the cumulative return
                              trajectory:
                            </p>
                            <ul className="list-disc ml-4 mb-2">
                              <li>
                                <strong>AI Portfolio:</strong> The proposed
                                allocation based on optimization
                              </li>
                              <li>
                                <strong>Benchmark:</strong> The selected market
                                index or reference portfolio
                              </li>
                              <li>
                                <strong>Alternative Allocations:</strong> Any
                                other portfolio configurations for comparison
                              </li>
                            </ul>
                            <p className="mb-2">
                              <strong>Colored dotted vertical lines</strong>{' '}
                              indicate when the latest portfolio allocation was
                              uploaded or generated for each scenario, marking
                              the point at which the portfolio composition was
                              last updated.
                            </p>
                            <p className="mt-2">
                              The methodology preserves realistic market
                              behavior (momentum, mean reversion, regime
                              changes) while incorporating forward-looking
                              return expectations, providing a statistically
                              grounded view of potential outcomes.
                            </p>
                          </div>

                          <div>
                            <h4
                              className="text-base font-semibold mb-2"
                              style={{ color: 'var(--bq-text-primary)' }}
                            >
                              Key Assumptions
                            </h4>
                            <ul className="list-disc ml-4 mb-0">
                              <li>
                                Historical patterns remain relevant for future
                                behavior
                              </li>
                              <li>
                                Markets exhibit similar volatility
                                characteristics going forward
                              </li>
                              <li>
                                No structural breaks or unprecedented events
                                occur
                              </li>
                              <li>
                                Asset returns follow conditional distributions
                                captured by GARCH
                              </li>
                            </ul>
                          </div>
                        </div>
                      }
                      width="44rem"
                    />
                  </div>
                </div>
                <div className="col-4">
                  <div className="text-center">
                    <div
                      className="text-base font-semibold"
                      style={{ color: 'var(--bq-chart-ai)' }}
                    >
                      {data?.projected_volatility_proposed?.toFixed(2) ||
                        '0.00'}
                      %
                    </div>
                    <div
                      className="text-xs"
                      style={{ color: 'var(--bq-text-tertiary)' }}
                    >
                      Projected Volatility
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
